import React, { useState, useEffect } from 'react'
import { toast } from 'react-hot-toast'
import { showErrorToast } from '@/utils/errorToast'
import { businessApi } from '@/services/endpoints/businessApi'
import { useAuth } from '@/stores/auth'
import { BusinessProfileForm } from '@/components/organisms/BusinessProfileForm'
import { TeamMembersOnboardingForm } from '@/components/organisms/TeamMembersOnboardingForm'
import { Stepper } from '@/components/ui/stepper'
import type { BusinessProfileData } from '@/services/endpoints/businessApi'
import type { BusinessProfileFormData } from '@/types/business-profile'
import type { Business } from '@/types'
import { Building2, Loader2 } from 'lucide-react'

interface TeamMember {
  firstName: string
  lastName: string
  email: string
}

const ONBOARDING_STEPS = [
  {
    id: 'business-profile',
    label: 'Business Profile',
    description: 'Company details',
  },
  {
    id: 'team-members',
    label: 'Team Members',
    description: 'Add your team',
  },
]

export const BusinessOnboardingPage: React.FC = () => {
  const { updateBusiness } = useAuth()
  const [currentStep, setCurrentStep] = useState(1)
  const [loading, setLoading] = useState(false)
  const [fetchLoading, setFetchLoading] = useState(true)
  const [businessData, setBusinessData] = useState<Business | null>(null)
  const [, setCompletedSteps] = useState<Set<number>>(new Set())

  // Fetch business profile on mount to prefill form
  useEffect(() => {
    const fetchBusinessProfile = async () => {
      try {
        const response = await businessApi.getProfile()
        const business = response.data.data as Business
        setBusinessData(business)
        updateBusiness(business)
      } catch (error) {
        console.error('Failed to fetch business profile:', error)
      } finally {
        setFetchLoading(false)
      }
    }

    fetchBusinessProfile()
  }, [updateBusiness])

  const handleBusinessProfileSubmit = async (data: BusinessProfileData) => {
    try {
      setLoading(true)
      await businessApi.updateProfile(data)
      toast.success('Business profile updated successfully!')

      // Mark step 1 as completed and move to step 2
      setCompletedSteps(prev => new Set([...prev, 1]))
      setCurrentStep(2)
    } catch (error) {
      // Error toast is already shown by the response interceptor
      // which displays the specific validation/API error message
      console.error('Failed to update business profile:', error)
    } finally {
      setLoading(false)
    }
  }

  const handleImageUpload = async (file: File) => {
    try {
      await businessApi.updateProfileImage(file)
      toast.success('Profile image updated successfully!')
    } catch (error) {
      console.error('Failed to upload image:', error)
      showErrorToast(error, 'Failed to upload image. Please try again.')
    }
  }

  const handleTeamMembersSubmit = async (members: TeamMember[]) => {
    try {
      setLoading(true)
      
      // TODO: Implement team members API call here
      console.log('Team members to add:', members)
      
      // Simulate API call
      await new Promise(resolve => setTimeout(resolve, 1000))
      
      if (members.length > 0) {
        toast.success(`Successfully added ${members.length} team member${members.length > 1 ? 's' : ''}!`)
      }
      
      completeOnboarding()
    } catch (error) {
      console.error('Failed to add team members:', error)
      showErrorToast(error, 'Failed to add team members. Please try again.')
    } finally {
      setLoading(false)
    }
  }

  const handleSkipTeamMembers = () => {
    completeOnboarding()
  }

  const completeOnboarding = () => {
    toast.success('Onboarding completed successfully! Welcome to your dashboard.')
    
    // Mark onboarding as completed and refresh to show dashboard
    setTimeout(() => {
      window.location.reload()
    }, 1500)
  }

  const transformBusinessToFormData = (business: Business | null): Partial<BusinessProfileFormData> => {
    if (!business) return {}

    return {
      name: business.name || '',
      description: business.description || '',
      website: business.website || '',
      industry: business.industry || '',
      designation: business.designation || '',
      company_size: business.company_size || '',
      contact_info: {
        email: business.contact_info?.email || '',
        phone: business.contact_info?.phone || '',
        address: {
          line1: typeof business.contact_info?.address === 'string'
            ? business.contact_info.address
            : business.contact_info?.address?.line1 || '',
          line2: typeof business.contact_info?.address === 'object'
            ? business.contact_info.address.line2 || ''
            : '',
          city: typeof business.contact_info?.address === 'object'
            ? business.contact_info.address.city || ''
            : '',
          state: typeof business.contact_info?.address === 'object'
            ? business.contact_info.address.state || ''
            : '',
          postal_code: typeof business.contact_info?.address === 'object'
            ? business.contact_info.address.postal_code || ''
            : '',
          country: typeof business.contact_info?.address === 'object'
            ? business.contact_info.address.country || ''
            : '',
        },
      },
    } as BusinessProfileFormData
  }

  if (fetchLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-slate-50 via-blue-50 to-indigo-50 dark:from-slate-900 dark:via-slate-800 dark:to-slate-900">
        <div className="flex items-center space-x-2">
          <Loader2 className="h-6 w-6 animate-spin" />
          <span>Loading business profile...</span>
        </div>
      </div>
    )
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-background via-blue-50/10 to-indigo-50/10 dark:from-background dark:via-blue-950/20 dark:to-indigo-950/20 p-4">
      <div className="max-w-5xl mx-auto pt-8">
        {/* Header */}
        <div className="text-center mb-8">
          <div className="inline-flex items-center justify-center w-16 h-16 bg-blue-100 dark:bg-blue-900/30 rounded-full mb-4">
            <Building2 className="h-8 w-8 text-blue-600 dark:text-blue-400" />
          </div>
          <h1 className="text-3xl font-bold text-foreground mb-2">
            Welcome to Just Ping
          </h1>
          <p className="text-muted-foreground max-w-2xl mx-auto">
            Let's set up your account to get you started with your conversational AI platform.
          </p>
        </div>

        {/* Stepper */}
        <div className="mb-8">
          <Stepper steps={ONBOARDING_STEPS} currentStep={currentStep} />
        </div>

        {/* Step Content */}
        <div className="mb-8">
          {currentStep === 1 && (
            <div>
              <div className="text-center mb-6">
                <h2 className="text-xl font-semibold text-foreground mb-2">
                  Complete Your Business Profile
                </h2>
                <p className="text-muted-foreground">
                  Tell us about your business to personalize your experience.
                </p>
              </div>
              
              <BusinessProfileForm
                initialData={transformBusinessToFormData(businessData)}
                currentImage={businessData?.profile_image}
                onSubmit={handleBusinessProfileSubmit}
                onImageUpload={handleImageUpload}
                loading={loading}
                className="bg-card backdrop-blur-sm border border-border shadow-xl"
              />
            </div>
          )}

          {currentStep === 2 && (
            <div>
              <div className="text-center mb-6">
                <h2 className="text-xl font-semibold text-foreground mb-2">
                  Add Your Team
                </h2>
                <p className="text-muted-foreground">
                  Invite team members to collaborate on your AI projects.
                </p>
              </div>
              
              <TeamMembersOnboardingForm
                onSubmit={handleTeamMembersSubmit}
                onSkip={handleSkipTeamMembers}
                loading={loading}
                className="bg-card backdrop-blur-sm border border-border shadow-xl"
              />
            </div>
          )}
        </div>
      </div>
    </div>
  )
}